import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { Category } from "@/lib/types";
import { getTranslatedText } from "@/lib/translations";

interface Props {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  productCountByCat: Record<string, number>;
  onSelect: (categoryId: string) => void;
}

export const CategoryPicker = ({
  open,
  onClose,
  categories,
  productCountByCat,
  onSelect,
}: Props): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("categoryPicker.ariaLabel", "Choisir une catégorie")}
      className="fixed inset-0 z-50 flex flex-col items-stretch sm:items-center sm:justify-center"
    >
      <button
        type="button"
        aria-label={t("common.close", "Fermer")}
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 animate-fade-in"
      />

      <div className="relative mt-auto sm:mt-0 sm:w-full sm:max-w-md bg-bg rounded-t-modal sm:rounded-modal max-h-[80vh] flex flex-col overflow-hidden animate-slide-up shadow-warm-lg pb-safe">
        <header className="px-5 pt-5 pb-3 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="display-font text-xl font-semibold text-ink leading-tight">
              {t("categoryPicker.title", "Catégories")}
            </h2>
            <p className="text-xs text-ink-soft mt-0.5">
              {t("categoryPicker.sections", { count: categories.length, defaultValue: `${categories.length} sections au menu` })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-surface shadow-warm-sm flex items-center justify-center text-ink"
            aria-label={t("common.close", "Fermer")}
          >
            ✕
          </button>
        </header>

        <ul className="flex-1 overflow-y-auto p-3 space-y-2">
          {categories.map((cat) => {
            const count = productCountByCat[cat.id] ?? 0;
            return (
              <li key={cat.id}>
                <button
                  type="button"
                  onClick={() => onSelect(cat.id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-4 rounded-button bg-surface shadow-warm-sm hover:bg-pink-softer transition-colors"
                >
                  <span className="display-font text-lg font-semibold text-ink truncate">
                    {getTranslatedText(cat, "name", i18n.language)}
                  </span>
                  <span className="text-sm text-ink-muted tabular-nums shrink-0">
                    {t("categoryPicker.items", { count, defaultValue: `${count} article${count > 1 ? "s" : ""}` })}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body
  );
};
